import { useState } from "react";
import { Upload, FileText, PlayCircle, Trash2, CheckCircle, Send, AlertCircle, Download, Database } from "lucide-react";
import { useCdrPipeline } from "../../hooks/useCdrPipeline";
import { LogStreamViewer } from "../../components/processors/Logstreamviewer";

export const CdrPipelineProcessor = () => {
  const [files, setFiles] = useState({
    pnn: null,
    clearcom: null,
    insight: null,
    khomp_log: null,
  });

  const [useTelegramBot, setUseTelegramBot] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  const { uploadAndProcess, cancel, loading, uploadProgress } = useCdrPipeline();

  const handleFileChange = (type, file) => {
    setFiles((prev) => ({ ...prev, [type]: file }));
  };

  const removeFile = (type) => {
    setFiles((prev) => ({ ...prev, [type]: null }));
  };

  // Deriva el modo según los archivos seleccionados
  const tieneKhomp = !!(files.insight && files.khomp_log);
  const tieneUnSoloKhomp = !!(files.insight) !== !!(files.khomp_log); // XOR

  const validateFiles = () => {
    const errors = [];
    if (!files.clearcom) errors.push("ClearCom es requerido");
    if (tieneUnSoloKhomp) errors.push("Si subes Insight debes subir Khomp Log también, y viceversa");
    return errors;
  };

  const getModoLabel = () => {
    if (tieneKhomp && files.pnn) return { texto: "Modo completo — ClearCom + Khomp + actualiza PNN en BD", color: "text-green-700", bg: "bg-green-50 border-green-200" };
    if (tieneKhomp) return { texto: "Modo completo — ClearCom + Khomp (usando PNN existente en BD)", color: "text-green-700", bg: "bg-green-50 border-green-200" };
    if (files.pnn) return { texto: "Modo solo ClearCom — actualiza PNN en BD", color: "text-blue-700", bg: "bg-blue-50 border-blue-200" };
    return { texto: "Modo solo ClearCom (usando PNN existente en BD)", color: "text-blue-700", bg: "bg-blue-50 border-blue-200" };
  };

  const handleSubmit = async () => {
    const errors = validateFiles();

    if (errors.length > 0) {
      alert("❌ Errores de validación:\n\n" + errors.join("\n"));
      return;
    }

    try {
      const result = await uploadAndProcess(files, {
        mode: "full",
        usePnnFromDB: false,
        useTelegramBot,
        onJobCreated: (id) => {
          setJobId(id);
        },
      });

      setJobStatus(result);
    } catch (error) {
      console.error("Error procesando:", error);
    }
  };

  const clearAll = () => {
    setFiles({
      pnn: null,
      clearcom: null,
      insight: null,
      khomp_log: null,
    });
    setJobId(null);
    setJobStatus(null);
    setUseTelegramBot(false);
  };

  const getTargetTable = () => {
    if (!jobStatus?.year || !jobStatus?.month) return null;
    return `contactacc${jobStatus.year}${jobStatus.month}`;
  };

  const FileUploadBox = ({ type, label, file, icon: Icon, required = true, accept = ".csv,.log", hint = null }) => (
    <div className="relative border-2 border-dashed rounded-lg p-4 hover:border-blue-400 transition-all hover:shadow-md">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Icon size={20} className="text-blue-600" />
          </div>
          <div>
            <span className="font-semibold text-gray-700">{label}</span>
            {required
              ? <span className="ml-1 text-red-500 text-sm">*</span>
              : <span className="ml-2 text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">opcional</span>
            }
            {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
          </div>
        </div>
        {file && (
          <button
            onClick={() => removeFile(type)}
            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Eliminar archivo"
          >
            <Trash2 size={18} />
          </button>
        )}
      </div>

      {/* File Info o Upload Area */}
      {file ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800 truncate">{file.name}</p>
              <p className="text-xs text-green-600 mt-1">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
        </div>
      ) : (
        <label className="cursor-pointer block">
          <input
            type="file"
            accept={accept}
            onChange={(e) => handleFileChange(type, e.target.files[0])}
            className="hidden"
          />
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 hover:bg-blue-50 transition-all text-center">
            <Upload className="mx-auto mb-2 text-gray-400" size={24} />
            <p className="text-sm text-gray-600">Click para seleccionar</p>
            <p className="text-xs text-gray-400 mt-1">{accept}</p>
          </div>
        </label>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-lg">
              <PlayCircle size={32} />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Pipeline CDR Completo</h1>
              <p className="text-blue-100 text-sm mt-1">
                ClearCom requerido — Insight, Khomp y PNN opcionales
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Configuración */}
          <div className="mb-6 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Database size={20} className="text-blue-600" />
              Configuración de Procesamiento
            </h3>

            <div className="space-y-3">
              {/* Modo detectado automáticamente */}
              {files.clearcom && (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium ${getModoLabel().bg} ${getModoLabel().color}`}>
                  <CheckCircle size={16} />
                  {getModoLabel().texto}
                </div>
              )}

              {/* Advertencia si solo uno de insight/khomp */}
              {tieneUnSoloKhomp && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg border bg-yellow-50 border-yellow-200 text-sm font-medium text-yellow-700">
                  <AlertCircle size={16} />
                  Para procesar Khomp necesitas subir Insight y Khomp Log juntos
                </div>
              )}

              {/* Telegram Bot */}
              <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 cursor-pointer transition-all">
                <input
                  type="checkbox"
                  checked={useTelegramBot}
                  onChange={(e) => setUseTelegramBot(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-700">📱 Enviar Notificaciones a Telegram</span>
                  <p className="text-sm text-gray-500 mt-1">
                    Recibe actualizaciones en tiempo real del progreso del pipeline y alertas de números desconocidos.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Grid de Archivos */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-gray-600" />
              Archivos de Entrada
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* ClearCom — REQUERIDO */}
              <FileUploadBox
                type="clearcom"
                label="ClearCom (Call Records)"
                file={files.clearcom}
                icon={FileText}
                required={true}
                accept=".csv"
              />
              {/* PNN — OPCIONAL: si se sube actualiza consumos.pnn en BD */}
              <FileUploadBox
                type="pnn"
                label="PNN (Plan de Numeración)"
                file={files.pnn}
                icon={FileText}
                required={false}
                accept=".csv"
                hint="Opcional — actualiza el PNN en BD al subir"
              />
              {/* Insight — OPCIONAL: necesario junto con Khomp para flujo completo */}
              <FileUploadBox
                type="insight"
                label="Insight (Tickets)"
                file={files.insight}
                icon={FileText}
                required={false}
                accept=".csv"
                hint="Opcional — requiere también Khomp Log"
              />
              {/* Khomp Log — OPCIONAL: necesario junto con Insight */}
              <FileUploadBox
                type="khomp_log"
                label="Khomp Log"
                file={files.khomp_log}
                icon={FileText}
                required={false}
                accept=".log,.csv"
                hint="Opcional — requiere también Insight"
              />
            </div>
          </div>

          {/* Barra de Progreso de Subida */}
          {loading && uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-800">
                  Subiendo archivos...
                </span>
                <span className="text-sm font-bold text-blue-600">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Botones de Acción */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  Procesando Pipeline...
                </>
              ) : (
                <>
                  <Send size={20} />
                  {useTelegramBot ? " Procesar con Telegram" : " Procesar Pipeline"}
                </>
              )}
            </button>

            <button
              onClick={clearAll}
              disabled={loading}
              className="px-8 bg-gray-200 text-gray-700 py-4 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
            >
              <Trash2 size={20} />
              Limpiar
            </button>
          </div>

          {/* Estado del Job */}
          {jobStatus && (
            <div className="mb-6 p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-300">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <CheckCircle size={20} className="text-green-600" />
                Resultado del Procesamiento
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Job ID */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Job ID</p>
                  <p className="font-mono text-sm text-gray-800 truncate">{jobStatus.id}</p>
                </div>

                {/* Estado */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Estado</p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${jobStatus.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : jobStatus.status === "failed"
                        ? "bg-red-100 text-red-800"
                        : jobStatus.status === "processing"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                  >
                    {jobStatus.status}
                  </span>
                </div>

                {/* Tabla Destino */}
                {getTargetTable() && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-500 mb-1">Tabla MySQL</p>
                    <p className="font-mono text-sm text-blue-600 font-semibold">
                      {getTargetTable()}
                    </p>
                  </div>
                )}

                {/* Año/Mes */}
                {jobStatus.year && jobStatus.month && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-500 mb-1">Período</p>
                    <p className="font-semibold text-gray-800">
                      {jobStatus.year}-{jobStatus.month}
                    </p>
                  </div>
                )}

                {/* Total Registros */}
                {jobStatus.total_records != null && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-500 mb-1">Registros Procesados</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {jobStatus.total_records.toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Duración */}
                {jobStatus.duration && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-500 mb-1">Duración</p>
                    <p className="font-semibold text-gray-800">{jobStatus.duration}</p>
                  </div>
                )}
              </div>

              {/* Botón de Descarga */}
              {jobStatus.output_file && (
                <div className="mt-4">
                  <a
                    href={`http://localhost:3000/cdr-pipeline/download/${jobStatus.output_file}`}
                    download
                    className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all shadow-md hover:shadow-lg"
                  >
                    <Download size={20} />
                    Descargar Archivo Procesado
                  </a>
                </div>
              )}

              {/* Error Message */}
              {jobStatus.error_message && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-800 mb-1">Error en el Procesamiento</p>
                    <p className="text-sm text-red-700">{jobStatus.error_message}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Logs en Tiempo Real */}
          {jobId && <LogStreamViewer jobId={jobId} />}
        </div>
      </div>
    </div>
  );
};

export default CdrPipelineProcessor;